/**
 * Cart Drawer & Notification
 */

class CartDrawer {
  constructor() {
    this.drawer = document.getElementById('cart-drawer');
    this.notification = document.getElementById('cart-notification');
    this.itemsContainer = document.getElementById('cart-drawer-items');
    
    if (!this.drawer) return;
    
    this.init();
    this.loadCart();
  }

  init() {
    // Close button
    const closeBtn = this.drawer.querySelector('.cart-drawer__close');
    if (closeBtn) {
      closeBtn.addEventListener('click', () => this.close());
    }

    // Overlay click
    const overlay = this.drawer.querySelector('.cart-drawer__overlay');
    if (overlay) {
      overlay.addEventListener('click', () => this.close());
    }

    // Notification close
    const notifClose = this.notification?.querySelector('.cart-notification__close');
    if (notifClose) {
      notifClose.addEventListener('click', () => this.hideNotification());
    }

    // Checkout button
    const checkoutBtn = this.drawer.querySelector('.cart-drawer__checkout');
    if (checkoutBtn) {
      checkoutBtn.addEventListener('click', () => {
        window.location.href = '/checkout';
      });
    }

    // Listen for add to cart events
    document.addEventListener('submit', (e) => {
      if (e.target.matches('form[action*="/cart/add"]')) {
        e.preventDefault();
        this.handleAddToCart(e.target);
      }
    });

    // Listen for cart icon clicks
    document.querySelectorAll('.header__cart-link').forEach(link => {
      link.addEventListener('click', (e) => {
        e.preventDefault();
        this.open();
      });
    });

    // منع السكرول على الصفحة لما الكارت مفتوح
    document.addEventListener('touchmove', (e) => {
      if (!this.drawer.classList.contains('is-open')) return;
      
      // لو اللمس مش جوه منطقة الـ items، امنعه
      const itemsArea = e.target.closest('.cart-drawer__items');
      if (!itemsArea && e.target.closest('.cart-drawer__container')) {
        e.preventDefault();
      }
    }, { passive: false });
  }

  async handleAddToCart(form) {
    const formData = new FormData(form);
    
    // Show loading state on button
    const submitButton = form.querySelector('[type="submit"]');
    const originalText = submitButton ? submitButton.textContent : '';
    if (submitButton) {
      submitButton.disabled = true;
      submitButton.textContent = 'Adding...';
    }

    // Store the current cart state
    let initialCart;
    try {
      const initialResponse = await fetch('/cart.js');
      initialCart = await initialResponse.json();
    } catch (e) {
      initialCart = { item_count: 0 };
    }

    // Try to add to cart - ignore any errors from this request
    try {
      await fetch('/cart/add.js', {
        method: 'POST',
        body: formData
      });
    } catch (e) {
      // Ignore fetch errors - we'll check cart state instead
    }

    // Wait for Shopify to update
    await new Promise(resolve => setTimeout(resolve, 600));

    // Check the actual cart state
    let finalCart;
    try {
      const finalResponse = await fetch('/cart.js');
      finalCart = await finalResponse.json();
    } catch (e) {
      finalCart = { item_count: initialCart.item_count };
    }

    // Restore button state
    if (submitButton) {
      submitButton.disabled = false;
      submitButton.textContent = originalText;
    }

    // Update cart display
    await this.loadCart();

    // If cart count increased, show success
    if (finalCart.item_count > initialCart.item_count) {
      this.showNotification();
      this.open();
    } else {
      // Cart didn't change - show alert only if really failed
      alert('Could not add product to cart. Please try again.');
    }
  }

  async loadCart() {
    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();

      this.updateCartCount(cart.item_count);
      this.updateCartTotal(cart.total_price);
      this.renderCartItems(cart.items);

    } catch (error) {
      console.error('Error loading cart:', error);
    }
  }

  updateCartCount(count) {
    // Update drawer count
    const drawerCount = this.drawer.querySelector('.cart-drawer__count');
    if (drawerCount) {
      drawerCount.textContent = count;
    }

    // Update header cart count
    document.querySelectorAll('[data-cart-count]').forEach(el => {
      if (count > 0) {
        el.innerHTML = `<span>${count}</span>`;
      } else {
        el.innerHTML = '';
      }
    });
  }

  updateCartTotal(totalPrice) {
    const totalEl = this.drawer.querySelector('.cart-drawer__subtotal-price');
    if (totalEl) {
      totalEl.textContent = this.formatMoney(totalPrice);
    }
  }

  renderCartItems(items) {
    if (!this.itemsContainer) return;

    if (items.length === 0) {
      this.itemsContainer.innerHTML = `
        <div class="cart-drawer__empty">
          <p>Your cart is empty</p>
        </div>
      `;
      return;
    }

    this.itemsContainer.innerHTML = items.map(item => `
      <div class="cart-drawer__item" data-key="${item.key}">
        <div class="cart-drawer__item-image">
          ${item.image ? `<img src="${item.image}" alt="${item.title}" loading="lazy">` : ''}
        </div>
        <div class="cart-drawer__item-details">
          <h3 class="cart-drawer__item-title">${item.product_title}</h3>
          ${item.variant_title ? `<p class="cart-drawer__item-variant">${item.variant_title}</p>` : ''}
          <p class="cart-drawer__item-price">${this.formatMoney(item.final_line_price)}</p>
        </div>
        <div class="cart-drawer__item-quantity">
          <div class="cart-drawer__quantity-controls">
            <button type="button" class="cart-drawer__quantity-btn" data-action="decrease" data-key="${item.key}">−</button>
            <span class="cart-drawer__quantity-value">${item.quantity}</span>
            <button type="button" class="cart-drawer__quantity-btn" data-action="increase" data-key="${item.key}">+</button>
          </div>
          <button type="button" class="cart-drawer__item-remove" data-remove data-key="${item.key}">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M12 4L4 12M4 4L12 12" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
            </svg>
          </button>
        </div>
      </div>
    `).join('');

    // Add event listeners for quantity controls
    this.itemsContainer.querySelectorAll('.cart-drawer__quantity-btn').forEach(btn => {
      btn.addEventListener('click', (e) => this.updateQuantity(e));
    });

    this.itemsContainer.querySelectorAll('[data-remove]').forEach(btn => {
      btn.addEventListener('click', (e) => this.removeItem(e));
    });
  }

  async updateQuantity(e) {
    const button = e.target.closest('.cart-drawer__quantity-btn');
    const key = button.dataset.key;
    const action = button.dataset.action;
    
    const valueEl = button.parentElement.querySelector('.cart-drawer__quantity-value');
    let quantity = parseInt(valueEl.textContent);

    if (action === 'increase') {
      quantity++;
    } else if (action === 'decrease' && quantity > 1) {
      quantity--;
    }

    try {
      await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: key, quantity })
      });

      await this.loadCart();
    } catch (error) {
      console.error('Error updating quantity:', error);
    }
  }

  async removeItem(e) {
    const button = e.target.closest('[data-remove]');
    const key = button.dataset.key;

    try {
      await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: key, quantity: 0 })
      });

      await this.loadCart();
    } catch (error) {
      console.error('Error removing item:', error);
    }
  }

  open() {
    this.drawer.classList.add('is-open');
    document.body.classList.add('cart-drawer-open');
    document.documentElement.classList.add('cart-drawer-open');
  }

  close() {
    this.drawer.classList.remove('is-open');
    document.body.classList.remove('cart-drawer-open');
    document.documentElement.classList.remove('cart-drawer-open');
  }

  showNotification() {
    if (!this.notification) return;
    
    this.notification.classList.add('is-visible');
    
    setTimeout(() => {
      this.hideNotification();
    }, 3000);
  }

  hideNotification() {
    if (!this.notification) return;
    this.notification.classList.remove('is-visible');
  }

  formatMoney(cents) {
    return (cents / 100).toLocaleString('en-US', {
      style: 'currency',
      currency: 'USD'
    });
  }
}

// Initialize
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new CartDrawer();
  });
} else {
  new CartDrawer();
}